import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import PageHeader from '@/components/feature/PageHeader';
import Button from '@/components/base/Button';
import { driversList } from '@/mocks/drivers';
import { useAppData } from '@/store/AppDataContext';

export default function AssignDriverPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId') || '';
  const orderNo = searchParams.get('orderNo') || '';
  const alertId = searchParams.get('alertId') || '';
  const back = searchParams.get('back') || '/orders';
  const { assignOrderDriver, removeAlert } = useAppData();

  const [selected, setSelected] = useState('');
  const availableDrivers = driversList.filter((driver) => driver.status === 'available');

  const submit = () => {
    if (!selected) return;
    const driver = availableDrivers.find((d) => d.id === selected);
    if (!driver) return;
    if (orderId) assignOrderDriver(orderId, driver.id, driver.name);
    if (alertId) removeAlert(alertId);
    navigate(back, {
      state: { toast: { type: 'success', message: `تم تعيين المهمة للمندوب ${driver.name}` } },
    });
  };

  return (
    <DashboardLayout title={`تعيين مندوب للطلب ${orderNo}`}>
      <div className="mx-auto w-full max-w-[560px]">
        <PageHeader
          title={`تعيين مندوب للطلب ${orderNo}`}
          subtitle="اختر مندوباً متاحاً لتعيين المهمة عليه."
          backTo={back}
        />

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          {availableDrivers.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-card border border-background-200 bg-background-50 px-4 py-8 text-center">
              <i className="ri-user-unfollow-line text-3xl text-foreground-400" aria-hidden="true"></i>
              <p className="mt-2 text-[13px] text-foreground-500">
                لا يوجد مندوبون متاحون حالياً. جرّب بعد قليل أو تواصل مع الإدارة.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableDrivers.map((driver) => {
                const isSelected = selected === driver.id;
                return (
                  <button
                    key={driver.id}
                    type="button"
                    onClick={() => setSelected(driver.id)}
                    className={[
                      'flex w-full items-center gap-3 rounded-card border p-3 text-right transition-colors',
                      isSelected
                        ? 'border-accent-400 bg-accent-50'
                        : 'border-background-200 bg-white hover:bg-background-50',
                    ].join(' ')}
                  >
                    <span className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-background-200">
                      <img src={driver.photo} alt={driver.name} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[14px] font-medium text-foreground-950">{driver.name}</span>
                        <span className="flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-medium text-success-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-success-500"></span>
                          {driver.statusLabel}
                        </span>
                      </span>
                      <span className="mt-0.5 flex items-center gap-3 text-[12px] text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line text-foreground-400" aria-hidden="true"></i>
                          {driver.distance}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500" aria-hidden="true"></i>
                          {driver.rating}
                        </span>
                      </span>
                    </span>
                    <span
                      className={[
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
                        isSelected
                          ? 'border-accent-500 bg-accent-500 text-foreground-950'
                          : 'border-background-300 text-transparent',
                      ].join(' ')}
                    >
                      <i className="ri-check-line text-sm" aria-hidden="true"></i>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex items-start gap-2 rounded-card bg-warning-50 px-3 py-2.5 text-[12px] leading-relaxed text-warning-700">
            <i className="ri-information-line mt-0.5 text-base shrink-0" aria-hidden="true"></i>
            التعيين إلزامي — المندوب سيستقبل المهمة مباشرةً بمجرد التأكيد.
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t border-background-100 pt-4 sm:flex-row">
            <Button variant="outline" size="lg" className="flex-1" onClick={() => navigate(back)}>
              إلغاء
            </Button>
            <Button
              variant="cta"
              size="lg"
              className="flex-1"
              icon="ri-user-follow-line"
              disabled={!selected}
              onClick={submit}
            >
              تعيين المندوب
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}